import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';

import {useDispatch} from 'react-redux';

import {ENDPOINTS} from 'constants/endpoints';
import {useGetQuoteItemsCartDataQueryKey} from 'desktopMiniCart/constants';
import {apiConfig} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';
import {CART_ITEMS_V2_QUERY_KEY} from 'mobile/hooks/useCartItemsV2/useCartItemsV2';
import {removeQuoteItem} from 'mobile/pages/cart/store/actions/cartItems/cartItems';
import {AppDispatchType} from 'mobile/store';

/**
 * react query hook for removing cart items
 */
export const useRemoveCartItem = (isCartPage = false) => {
    const queryClient = useQueryClient();
    const {dispatch: dispatchToHost} = apiConfig.hostStore;
    const dispatch = useDispatch<AppDispatchType>();

    const {isPending, mutate} = useMutation({
        mutationFn: (itemId: number) =>
            apiConfig.instanceGusaBackend({
                method: 'get',
                url: `${ENDPOINTS.REMOVE_QUOTE_ITEM}/${itemId}`,
            }),
        onError: async (error: AxiosError<never>) => {
            logWithSentry(error, 'Could not remove cart item');
            if (__BUILD_TYPE__ === 'mobile') {
                if (isCartPage) {
                    dispatch({
                        type: `${removeQuoteItem.rejected}`,
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ['cartItemsV2', ENDPOINTS.GET_CART_ITEMS_V2],
                    });
                }
            }
        },
        onSuccess: async (response, itemId) => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: useGetQuoteItemsCartDataQueryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: CART_ITEMS_V2_QUERY_KEY,
                }),
            ]);
            dispatchToHost({
                type: 'REFETCH_QUOTE',
            });
            if (__BUILD_TYPE__ === 'mobile') {
                dispatch({
                    meta: {
                        id: itemId,
                    },
                    payload: response,
                    type: `${removeQuoteItem.fulfilled}`,
                });
            }
        },
    });

    return {isLoading: isPending, removeCartItem: mutate};
};
